import { GlobalState } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { AuthBrowserApi, AuthPushTokenApi } from '@/openapi-clients-generated/auth';

import { Auth } from './auth.service';
import { Logger } from './logger';
import { serviceWorkerManager } from './sw.manager';

const logger = Logger.createScoped('BrowserPush');
let registrationInFlight: Promise<boolean> | undefined;

export const BrowserPush = {
    init() {
        serviceWorkerManager.onMessage('ackNotifications', data => {
            void this.ackNotifications(data.notificationIds).catch(err => logger.warn('Notification acknowledgement failed', err));
        });
        if (!('serviceWorker' in navigator)) return;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            const pending = registrationInFlight ?? Promise.resolve();
            void pending.finally(() => this.refreshNotificationRegistration());
        });
        window.addEventListener('online', () => this.refreshNotificationRegistration());
        window.addEventListener('focus', () => this.refreshNotificationRegistration());
    },

    refreshNotificationRegistration() {
        if (!Auth.activeJwt || !('Notification' in window)) return;
        if (Notification.permission === 'granted') void this.registerNotifications();
    },

    async registerNotifications(): Promise<boolean> {
        if (MobileBridge.isWithinZynoSuiteMobile || GlobalState.isWorkstation) return false;
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
        if (registrationInFlight) return registrationInFlight;
        // Request permission directly from the click handler, before any network or worker awaits.
        const permission = Notification.permission === 'granted' ? Promise.resolve('granted') : Notification.requestPermission();
        registrationInFlight = (async () => {
            const result = await permission;
            if (result !== 'granted') return false;
            const register = () => this.registerSubscription();
            const registered = navigator.locks ? await navigator.locks.request('zs:browserPushRegistration', register) : await register();
            return registered;
        })()
            .catch(err => {
                logger.warn('Browser push registration failed', err);
                return false;
            })
            .finally(() => {
                registrationInFlight = undefined;
            });
        return registrationInFlight;
    },

    async registerSubscription(): Promise<boolean> {
        const config = await AuthPushTokenApi.getPushTokenWebPushConfig({ throwOnError: true });
        if (!config.data?.vapidKey) return false;
        const ready = navigator.serviceWorker.ready;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const registration = await Promise.race([
            ready,
            new Promise<never>((_resolve, reject) => {
                timer = setTimeout(() => reject(new Error('Service worker is not ready')), 10_000);
            })
        ]).finally(() => clearTimeout(timer));
        const pushKey = await this.getPushKey();
        const prepared = await serviceWorkerManager.sendRequest('prepareBrowserPush', { pushKey }, 2000, registration.active ?? undefined);
        if (prepared?.version !== 1) {
            await registration.update();
            return false;
        }

        const applicationServerKey = decodeVapidKey(config.data.vapidKey);
        let subscription = await registration.pushManager.getSubscription();
        const currentKey = subscription?.options.applicationServerKey;
        const matchesKey = currentKey && Array.from(new Uint8Array(currentKey)).join(',') === Array.from(applicationServerKey).join(',');
        if (subscription && !matchesKey) {
            // An older app page can still manipulate this shared subscription. Rotate once those tabs close.
            if (prepared.otherClients) {
                logger.warn('Close other Zyno tabs before replacing the browser push subscription.');
                return false;
            }
            const unsubscribed = await subscription.unsubscribe();
            if (!unsubscribed) throw new Error('Could not replace the previous push subscription');
            subscription = null;
        }
        if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
        const json = subscription.toJSON();
        if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Browser returned an incomplete push subscription');
        await AuthPushTokenApi.postPushTokenRegisterWebPush({
            body: {
                subscription: {
                    endpoint: json.endpoint,
                    expirationTime: json.expirationTime,
                    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
                },
                vapidKey: config.data.vapidKey,
                key: pushKey
            },
            throwOnError: true
        });
        logger.info('Browser push registration complete');
        return true;
    },

    async getPushKey() {
        let pushKey = localStorage.getItem('zs:pushKey');
        if (!pushKey) {
            const key = await crypto.subtle.generateKey(
                {
                    name: 'AES-GCM',
                    length: 256
                },
                true,
                ['encrypt', 'decrypt']
            );
            const exportedKey = await crypto.subtle.exportKey('raw', key);
            pushKey = btoa(String.fromCharCode(...new Uint8Array(exportedKey)));
            localStorage.setItem('zs:pushKey', pushKey);
        }
        return pushKey;
    },

    async ackNotifications(notificationIds: string[]) {
        for (const notificationId of notificationIds) {
            await AuthBrowserApi.postBrowserAckNotification({ path: { notificationId }, throwOnError: true });
        }
    }
};

export function decodeVapidKey(value: string): Uint8Array<ArrayBuffer> {
    const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(binary, character => character.charCodeAt(0));
}

BrowserPush.init();
